import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  Button,
  Stack,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

/** Defaults alinhados a xpro4api (PT) — comparação de “não personalizado”. */
export const WEBCHAT_DEFAULT_BUTTON_LABEL = "Continuar";
export const WEBCHAT_DEFAULT_MESSAGE =
  "Para continuar o atendimento, toque no botão abaixo. Vamos abrir a conversa em uma nova aba segura.";

/** Textos padrão antigos — tratados como “não personalizado” ao editar o canal */
const LEGACY_DEFAULT_MESSAGES = new Set([
  WEBCHAT_DEFAULT_MESSAGE,
  "Para continuar o atendimento, toque no botão abaixo.",
  "Para continuar o atendimento sem custos adicionais no WhatsApp, toque no botão abaixo e converse conosco pelo chat.",
]);

function asBool(v) {
  return v === true || v === 1 || v === "1";
}

function isDefaultMessage(value) {
  const v = String(value || "").trim();
  return !v || LEGACY_DEFAULT_MESSAGES.has(v);
}

/**
 * Seção progressiva: liga o recurso → escolhe comportamento → avançado sob demanda.
 */
export default function WebchatRedirectSection({ formData, onChange }) {
  const { t } = useTranslation("settings");
  const enabled = asBool(formData?.webchat_redirect_enabled);

  const defaultButtonLabel = t("channelForm.webchat.defaultButtonLabel", {
    defaultValue: WEBCHAT_DEFAULT_BUTTON_LABEL,
  });
  const defaultMessage = t("channelForm.webchat.defaultMessage", {
    defaultValue: WEBCHAT_DEFAULT_MESSAGE,
  });

  const storedButton = String(formData?.webchat_redirect_button_label ?? "").trim();
  const storedMessage = String(formData?.webchat_redirect_message ?? "").trim();
  const hasCustomCopy =
    (storedButton &&
      storedButton !== WEBCHAT_DEFAULT_BUTTON_LABEL &&
      storedButton !== defaultButtonLabel) ||
    (storedMessage && !isDefaultMessage(storedMessage) && storedMessage !== defaultMessage);

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [customizeCopy, setCustomizeCopy] = useState(hasCustomCopy);

  const mode = useMemo(() => {
    const m = String(formData?.webchat_redirect_mode || "").trim();
    if (
      m === "always_redirect" ||
      m === "redirect_then_allow" ||
      m === "redirect_until_web"
    ) {
      return m;
    }
    return "always_redirect";
  }, [formData?.webchat_redirect_mode]);

  const once = formData?.webchat_redirect_once == null ? true : asBool(formData.webchat_redirect_once);

  const handleEnable = (checked) => {
    onChange("webchat_redirect_enabled", checked ? 1 : 0);
    if (checked) {
      if (!formData?.webchat_redirect_mode) {
        onChange("webchat_redirect_mode", "always_redirect");
      }
      if (formData?.webchat_redirect_once == null) {
        onChange("webchat_redirect_once", 1);
      }
    }
  };

  const handleCustomizeCopy = (checked) => {
    setCustomizeCopy(checked);
    if (!checked) {
      // Volta ao default do servidor (null = usa texto padrão na API)
      onChange("webchat_redirect_button_label", null);
      onChange("webchat_redirect_message", null);
    } else {
      onChange(
        "webchat_redirect_button_label",
        storedButton || defaultButtonLabel
      );
      onChange("webchat_redirect_message", storedMessage || defaultMessage);
    }
  };

  return (
    <Grid item xs={12}>
      <Divider sx={{ my: 1.5 }} />
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
        {t("channelForm.webchat.sectionTitle")}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.25 }}>
        {t("channelForm.webchat.sectionCaption")}
      </Typography>

      <FormControlLabel
        control={
          <Switch checked={enabled} onChange={(e) => handleEnable(e.target.checked)} />
        }
        label={t("channelForm.webchat.enabled")}
      />
      <FormHelperText sx={{ mt: 0, mb: enabled ? 1.5 : 0 }}>
        {t("channelForm.webchat.enabledHelper")}
      </FormHelperText>

      <Collapse in={enabled} unmountOnExit>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="webchat-mode-label">{t("channelForm.webchat.mode")}</InputLabel>
            <Select
              labelId="webchat-mode-label"
              label={t("channelForm.webchat.mode")}
              value={mode}
              onChange={(e) => onChange("webchat_redirect_mode", e.target.value)}
            >
              <MenuItem value="always_redirect">{t("channelForm.webchat.modeAlways")}</MenuItem>
              <MenuItem value="redirect_then_allow">{t("channelForm.webchat.modeThenAllow")}</MenuItem>
              <MenuItem value="redirect_until_web">{t("channelForm.webchat.modeUntilWeb")}</MenuItem>
            </Select>
            <FormHelperText>{t(`channelForm.webchat.modeHint.${mode}`)}</FormHelperText>
          </FormControl>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={customizeCopy}
                  onChange={(e) => handleCustomizeCopy(e.target.checked)}
                />
              }
              label={t("channelForm.webchat.customizeCopy")}
            />
            <FormHelperText sx={{ mt: 0 }}>
              {t("channelForm.webchat.customizeCopyHelper", {
                button: defaultButtonLabel,
              })}
            </FormHelperText>
            {!customizeCopy ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 1,
                  p: 1.25,
                  borderRadius: 1,
                  bgcolor: "action.hover",
                  whiteSpace: "pre-wrap",
                }}
              >
                {defaultMessage}
              </Typography>
            ) : null}
          </Box>

          <Collapse in={customizeCopy} unmountOnExit>
            <Stack spacing={1.5}>
              <TextField
                size="small"
                label={t("channelForm.webchat.buttonLabel")}
                value={storedButton || defaultButtonLabel}
                onChange={(e) =>
                  onChange(
                    "webchat_redirect_button_label",
                    String(e.target.value || "").slice(0, 20)
                  )
                }
                helperText={t("channelForm.webchat.buttonLabelHelper")}
                inputProps={{ maxLength: 20 }}
                fullWidth
              />
              <TextField
                size="small"
                label={t("channelForm.webchat.message")}
                value={storedMessage || defaultMessage}
                onChange={(e) => onChange("webchat_redirect_message", e.target.value)}
                helperText={t("channelForm.webchat.messageHelper")}
                fullWidth
                multiline
                minRows={3}
              />
            </Stack>
          </Collapse>

          <Box>
            <Button
              size="small"
              color="inherit"
              endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setShowAdvanced((v) => !v)}
              sx={{ px: 0.5, textTransform: "none", color: "text.secondary" }}
            >
              {t("channelForm.webchat.advancedToggle")}
            </Button>

            <Collapse in={showAdvanced} unmountOnExit>
              <Box sx={{ mt: 1.25, pl: { xs: 0, sm: 0.5 } }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={once}
                      onChange={(e) =>
                        onChange("webchat_redirect_once", e.target.checked ? 1 : 0)
                      }
                    />
                  }
                  label={t("channelForm.webchat.once")}
                />
                  <FormHelperText sx={{ mt: 0 }}>{t("channelForm.webchat.onceHelper")}</FormHelperText>
                <TextField
                  sx={{ mt: 1.5 }}
                  type="number"
                  size="small"
                  label={t("channelForm.webchat.nudgeMinutes")}
                  helperText={t("channelForm.webchat.nudgeMinutesHelper")}
                  value={formData?.webchat_nudge_minutes ?? 30}
                  onChange={(e) =>
                    onChange(
                      "webchat_nudge_minutes",
                      Math.max(0, Number(e.target.value) || 0)
                    )
                  }
                  inputProps={{ min: 0, max: 1440 }}
                  fullWidth
                />
                <TextField
                  sx={{ mt: 1.5 }}
                  size="small"
                  label={t("channelForm.webchat.nudgeMessage")}
                  helperText={t("channelForm.webchat.nudgeMessageHelper")}
                  value={formData?.webchat_nudge_message ?? ""}
                  onChange={(e) => onChange("webchat_nudge_message", e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                />
                <TextField
                  sx={{ mt: 1.5 }}
                  type="number"
                  size="small"
                  label={t("channelForm.webchat.autoCloseHours")}
                  helperText={t("channelForm.webchat.autoCloseHoursHelper")}
                  value={formData?.webchat_auto_close_hours ?? 0}
                  onChange={(e) =>
                    onChange(
                      "webchat_auto_close_hours",
                      Math.max(0, Number(e.target.value) || 0)
                    )
                  }
                  inputProps={{ min: 0, max: 168 }}
                  fullWidth
                />
              </Box>
            </Collapse>
          </Box>
        </Stack>
      </Collapse>
    </Grid>
  );
}

export function isWhatsappWebchatChannelTipo(tipo) {
  const n = Number(tipo);
  return n === 6 || n === 20 || n === 24;
}
